import { css } from "@emotion/react";
import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React, { useEffect, useRef } from "react";

import BaseLink from "~/components/links/BaseLink";

import SubNavItem from "~/components/SubNavItem";

import transition from "~/utilities/transition";
import trackNavigationEvent from "~/utilities/trackNavigationEvent";
import { sanitizeForId } from "~/utilities/helpers";
import { setTertiaryNavPosition } from "~/utilities/headerHelpers";

import useOnWindowResize from "~/hooks/useOnWindowResize";

const sharedNavItemStyles = ({ theme }) => {
    return css`
        ${transition({}, "text-decoration", "background-color")};
        width: 100%;
        text-align: left;
        padding: 8px 30px;
        font-size: 1.8rem;
        line-height: 1.5;
        color: ${theme.palette.black};
        text-decoration: none;
        background-color: transparent;
    `;
};

const Wrapper = styled.div`
    ${transition({}, "opacity", "visibility", "height")};
    background-color: ${({ theme, tertiaryLevel }) =>
        tertiaryLevel
            ? `rgba(255, 255, 255, 0.97)`
            : theme.colors.backgroundColor.defaultLight};
    box-shadow: ${({ tertiaryLevel }) =>
        tertiaryLevel
            ? `rgba(0, 0, 0, 0.06) 4px 2px 4px 2px`
            : `rgba(0, 0, 0, 0.06) 0 2px 4px 2px`};
    padding: 0;
    opacity: ${({ isOpen }) => (isOpen ? `1` : `0`)};
    visibility: ${({ isOpen }) => (isOpen ? `visible` : `hidden`)};
    z-index: ${({ theme }) => theme.zIndex.navWrapper};
    width: ${({ theme }) => theme.layout.subNavWidth};
    position: absolute;
    top: 100%;
    left: 0;

    &::before {
        background-color: ${({ theme, tertiaryLevel }) =>
            tertiaryLevel ? `transparent` : theme.palette.white};
        height: ${({ tertiaryLevel }) => (tertiaryLevel ? `100%` : `7px`)};
        width: ${({ tertiaryLevel }) => (tertiaryLevel ? `0px` : `auto`)};
        content: "";
        position: absolute;
        top: ${({ tertiaryLevel }) => (tertiaryLevel ? `0` : `-7px`)};
        left: 0;
        right: 0;
        display: block;
        box-shadow: ${({ tertiaryLevel }) =>
            tertiaryLevel ? `rgba(0, 0, 0, 0.06) 1px 0 4px 1px` : `none`};
    }
`;

const InnerWrapper = styled.div`
    position: relative;
`;

const SubNavHeading = styled.h4``;
const SecondaryHeading = styled.h3``;

const HeadingLink = styled(BaseLink)`
    ${sharedNavItemStyles};
    display: block;
    color: ${({ theme }) => theme.palette.purple};
    font-size: ${({ tertiaryLevel }) => (tertiaryLevel ? `1.8rem` : `2rem`)};
    font-weight: 700;
    padding: 13px 30px;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletLandscape}) {
        &:hover,
        &:focus {
            text-decoration: underline;
            background-color: ${({ theme }) => theme.palette.grayFive};
        }
    }
`;

const HeadingSpan = styled.span`
    ${sharedNavItemStyles};
    display: block;
    color: ${({ theme }) => theme.palette.purple};
    font-size: ${({ tertiaryLevel }) => (tertiaryLevel ? `1.8rem` : `2rem`)};
    font-weight: 700;
`;

export const SubItems = styled.ul`
    list-style: none;
`;

export const SubItem = styled.li``;

export const SubLink = styled(BaseLink)`
    ${sharedNavItemStyles};
    display: block;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletLandscape}) {
        &:hover,
        &:focus {
            text-decoration: underline;
            background-color: ${({ theme }) => theme.palette.grayFive};
        }
    }
`;

const Subnav = ({
    items,
    isOpen = false,
    topLinkLabel,
    topLinkUrl = "",
    topLinkNewTabOverride = "default",
    openedSubnavs = {},
    dispatch = () => {},
    level = "secondary",
    firstLinkLabel = "",
    secondarySubRefProp = {},
    tertiarySubRefProp = {},
}) => {
    const headingRef = useRef();
    const firstLinkRef = useRef();
    const tertiaryLevel = level === "tertiary";

    const wrapperProps = {
        isOpen,
        tertiaryLevel,
    };

    if (isOpen) {
        wrapperProps.ref = tertiaryLevel
            ? tertiarySubRefProp
            : secondarySubRefProp;
    }

    useEffect(() => {
        if (isOpen) {
            setTertiaryNavPosition(secondarySubRefProp, tertiarySubRefProp);
        }
    }, [isOpen, secondarySubRefProp, tertiarySubRefProp]);

    useOnWindowResize(() => {
        if (isOpen) {
            setTertiaryNavPosition(secondarySubRefProp, tertiarySubRefProp);
        }
    }, true);

    const ConditionalHeading = topLinkUrl ? HeadingLink : HeadingSpan;
    const headingProps = topLinkUrl
        ? {
              topLinkLabel: tertiaryLevel
                  ? sanitizeForId(firstLinkLabel)
                  : sanitizeForId(topLinkLabel),
              to: topLinkUrl,
              newTabOverride: topLinkNewTabOverride,
              onClick: () =>
                  trackNavigationEvent(
                      false,
                      tertiaryLevel ? 2 : 1,
                      tertiaryLevel
                          ? `${firstLinkLabel} > ${topLinkLabel}`
                          : topLinkLabel,
                  ),
              tagRef: headingRef,
              tertiaryLevel,
          }
        : {
              tertiaryLevel,
          };

    return (
        <Wrapper {...wrapperProps}>
            <InnerWrapper>
                {level === "secondary" ? (
                    <SecondaryHeading
                        id={`SubNavHeading-${
                            tertiaryLevel
                                ? `${sanitizeForId(
                                      firstLinkLabel,
                                  )}-${sanitizeForId(topLinkLabel)}`
                                : sanitizeForId(topLinkLabel)
                        }`}
                    >
                        <ConditionalHeading {...headingProps}>
                            {topLinkLabel}
                        </ConditionalHeading>
                    </SecondaryHeading>
                ) : (
                    <SubNavHeading
                        id={`SubNavHeading-${
                            tertiaryLevel
                                ? `${sanitizeForId(
                                      firstLinkLabel,
                                  )}-${sanitizeForId(topLinkLabel)}`
                                : sanitizeForId(topLinkLabel)
                        }`}
                    >
                        <ConditionalHeading {...headingProps}>
                            {topLinkLabel}
                        </ConditionalHeading>
                    </SubNavHeading>
                )}
                <SubItems
                    aria-labelledby={`SubNavHeading-${
                        tertiaryLevel
                            ? `${sanitizeForId(firstLinkLabel)}-${sanitizeForId(
                                  topLinkLabel,
                              )}`
                            : sanitizeForId(topLinkLabel)
                    }`}
                >
                    {items.map(
                        (
                            { linkLabel, linkUrl, newTabOverride, subItems },
                            index,
                        ) => (
                            <SubNavItem
                                key={linkLabel}
                                lastItem={index + 1 === items.length}
                                tagRef={index === 0 ? firstLinkRef : null}
                                {...{
                                    linkLabel,
                                    linkUrl,
                                    newTabOverride,
                                    subItems,
                                    topLinkLabel,
                                    index,
                                    openedSubnavs,
                                    dispatch,
                                    level,
                                    firstLinkLabel,
                                    tertiarySubRefProp,
                                }}
                            />
                        ),
                    )}
                </SubItems>
            </InnerWrapper>
        </Wrapper>
    );
};

Subnav.propTypes = {
    items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    isOpen: PropTypes.bool,
    topLinkLabel: PropTypes.string.isRequired,
    topLinkUrl: PropTypes.string,
    topLinkNewTabOverride: PropTypes.string,
    openedSubnavs: PropTypes.shape({}),
    dispatch: PropTypes.func,
    level: PropTypes.string,
    firstLinkLabel: PropTypes.string,
    secondarySubRefProp: PropTypes.object,
    tertiarySubRefProp: PropTypes.object,
};

export default Subnav;
